<template>
  <div class="team-upload-page">
    <div class="container mt-4">
      <div class="row justify-content-center">
        <div class="col-md-8">
          <div class="card">
            <div class="card-header">
              <h3 class="mb-0">Nahrát fotky a videa</h3>
              <p class="text-muted mb-0">Pouze pro týmy - nahrávání obsahu do galerie</p>
            </div>
            <div class="card-body">
              <!-- Login Form -->
              <div v-if="!isLoggedIn" class="login-section">
                <h4>Přihlášení</h4>
                <form @submit.prevent="login">
                  <div class="mb-3">
                    <label for="username" class="form-label">Uživatelské jméno</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="username" 
                      v-model="loginForm.username" 
                      required
                    />
                  </div>
                  <div class="mb-3">
                    <label for="password" class="form-label">Heslo</label>
                    <input 
                      type="password" 
                      class="form-control" 
                      id="password" 
                      v-model="loginForm.password" 
                      required
                    />
                  </div>
                  <button type="submit" class="btn btn-primary" :disabled="loggingIn">
                    {{ loggingIn ? 'Přihlašuji...' : 'Přihlásit' }}
                  </button>
                  <div v-if="loginError" class="alert alert-danger mt-3">
                    {{ loginError }}
                  </div>
                </form>
              </div>

              <!-- Upload Form -->
              <div v-else class="upload-section">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h4>Nahrát nové médium</h4>
                  <button @click="logout" class="btn btn-outline-secondary btn-sm">
                    Odhlásit
                  </button>
                </div>

                <form @submit.prevent="uploadMedia" enctype="multipart/form-data">
                  <div class="mb-3">
                    <label for="media" class="form-label">Soubor</label>
                    <input 
                      type="file" 
                      class="form-control" 
                      id="media" 
                      ref="fileInput" 
                      @change="onFileChange" 
                      accept="image/*,video/*" 
                      required 
                    />
                    <small class="text-muted d-block mt-1">
                      Povolené formáty: obrázky (JPEG, PNG, GIF) a videa (MP4, MOV, AVI). 
                      Maximální velikost: 100MB.
                    </small>
                  </div>

                  <div class="mb-3">
                    <label for="title" class="form-label">Název</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="title" 
                      v-model="uploadForm.title" 
                      placeholder="Např. Gól v zápase proti Blackhawks"
                      required 
                    />
                  </div>

                  <div class="mb-3">
                    <label for="description" class="form-label">Popis</label>
                    <textarea 
                      class="form-control" 
                      id="description" 
                      v-model="uploadForm.description" 
                      rows="3"
                      placeholder="Popište co se na fotce/videu děje..."
                    ></textarea>
                  </div>

                  <div class="mb-3">
                    <label for="event_date" class="form-label">Datum události</label>
                    <input 
                      type="date" 
                      class="form-control" 
                      id="event_date" 
                      v-model="uploadForm.event_date" 
                    />
                  </div>

                  <button type="submit" class="btn btn-primary" :disabled="uploading">
                    {{ uploading ? 'Nahrávám...' : 'Nahrát' }}
                  </button>
                  
                  <div v-if="uploadProgress" class="mt-2">
                    <div class="progress">
                      <div 
                        class="progress-bar" 
                        :style="{ width: uploadProgress + '%' }"
                      ></div>
                    </div>
                    <small class="text-muted">{{ uploadProgress }}%</small>
                  </div>

                  <div v-if="uploadError" class="alert alert-danger mt-3">
                    {{ uploadError }}
                  </div>

                  <div v-if="uploadSuccess" class="alert alert-success mt-3">
                    Soubor byl úspěšně nahrán!
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TeamUpload',
  data() {
    return {
      isLoggedIn: false,
      loggingIn: false,
      loginForm: {
        username: '',
        password: ''
      },
      loginError: '',
      uploadForm: {
        title: '',
        description: '',
        event_date: ''
      },
      uploading: false,
      uploadProgress: 0,
      uploadError: '',
      uploadSuccess: false
    };
  },
  mounted() {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      this.isLoggedIn = true;
    }
  },
  methods: {
    async login() {
      this.loggingIn = true;
      this.loginError = '';
      
      try {
        const response = await axios.post('/api/auth/login', this.loginForm);
        localStorage.setItem('token', response.data.token);
        this.isLoggedIn = true;
        this.loginForm = { username: '', password: '' };
      } catch (error) {
        this.loginError = 'Neplatné přihlašovací údaje';
        console.error('Login error:', error);
      } finally {
        this.loggingIn = false;
      }
    },
    
    logout() {
      localStorage.removeItem('token');
      this.isLoggedIn = false;
      this.uploadForm = { title: '', description: '', event_date: '' };
      this.uploadError = '';
      this.uploadSuccess = false;
    },
    
    onFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Auto-fill title if empty
        if (!this.uploadForm.title) {
          this.uploadForm.title = file.name.replace(/\.[^/.]+$/, "");
        }
      }
    },
    
    async uploadMedia() {
      this.uploading = true;
      this.uploadError = '';
      this.uploadSuccess = false;
      this.uploadProgress = 0;
      
      const formData = new FormData();
      const fileInput = this.$refs.fileInput;
      
      if (!fileInput.files[0]) {
        this.uploadError = 'Vyberte soubor k nahrání';
        this.uploading = false;
        return;
      }
      
      formData.append('media', fileInput.files[0]);
      formData.append('title', this.uploadForm.title);
      formData.append('description', this.uploadForm.description);
      formData.append('event_date', this.uploadForm.event_date);
      
      try {
        await axios.post('/api/team/gallery', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          }
        });
        
        this.uploadSuccess = true;
        this.uploadForm = { title: '', description: '', event_date: '' };
        fileInput.value = '';
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.uploadSuccess = false;
        }, 3000);
        
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.uploadError = 'Nemáte oprávnění k nahrávání. Pouze týmy mohou nahrávat obsah.';
        } else if (error.response && error.response.data && error.response.data.error) {
          this.uploadError = error.response.data.error;
        } else {
          this.uploadError = 'Chyba při nahrávání souboru';
        }
        console.error('Upload error:', error);
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
      }
    }
  }
};
</script>

<style scoped>
.team-upload-page {
  min-height: 100vh;
  background: #0a1023;
  color: #e0e6f7;
}

.card {
  background: #151d3a;
  border: 1px solid rgba(31, 224, 255, 0.08);
  border-radius: 0.75rem;
  color: #e0e6f7;
}

.card-header {
  background: rgba(31, 224, 255, 0.04);
  border-bottom: 1px solid rgba(31, 224, 255, 0.08);
  color: #e0e6f7;
}

.login-section, .upload-section {
  padding: 1rem 0;
}

.form-control {
  background: #0a1023;
  color: #e0e6f7;
  border: 1px solid rgba(31, 224, 255, 0.13);
  border-radius: 0.5rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.form-control:focus {
  background: #0a1023;
  color: #e0e6f7;
  border-color: #1fe0ff;
  box-shadow: 0 0 0 3px rgba(31, 224, 255, 0.15);
}

.form-label {
  color: #8892b0;
  font-weight: 500;
  font-size: 0.9rem;
}

.btn-primary {
  background: linear-gradient(135deg, #1fe0ff 0%, #0a7cff 100%);
  color: #0a1023;
  font-weight: 700;
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(31, 224, 255, 0.2);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(31, 224, 255, 0.35);
  color: #0a1023;
}

.btn-outline-secondary {
  color: #8892b0;
  border-color: rgba(136, 146, 176, 0.3);
  border-radius: 0.5rem;
  font-weight: 600;
}

.btn-outline-secondary:hover {
  background: rgba(136, 146, 176, 0.1);
  color: #e0e6f7;
}

.progress {
  height: 1rem;
  background: #0a1023;
  border-radius: 0.5rem;
  overflow: hidden;
}

.progress-bar {
  background: linear-gradient(135deg, #1fe0ff, #0a7cff);
  transition: width 0.3s ease;
}

.alert-danger {
  background: rgba(255, 77, 106, 0.1);
  color: #ff4d6a;
  border: 1px solid rgba(255, 77, 106, 0.2);
  border-radius: 0.75rem;
}

.alert-success {
  background: rgba(52, 211, 153, 0.1);
  color: #34d399;
  border: 1px solid rgba(52, 211, 153, 0.2);
  border-radius: 0.75rem;
}

.text-muted {
  color: #8892b0 !important;
}

.text-danger {
  color: #ff4d6a !important;
}

.text-success {
  color: #34d399 !important;
}
</style>
